import React, { useState } from 'react';
import {
  Box, Paper, Typography, Button, Stack, CircularProgress,
  Alert, Tooltip, MenuItem, TextField, Chip, LinearProgress,
} from '@mui/material';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import StorageIcon from '@mui/icons-material/Storage';
import SearchIcon from '@mui/icons-material/Search';
import ImageIcon from '@mui/icons-material/Image';
import CloudIcon from '@mui/icons-material/Cloud';
import FaceIcon from '@mui/icons-material/Face';
import RefreshIcon from '@mui/icons-material/Refresh';
import LockIcon from '@mui/icons-material/Lock';
import ApiIcon from '@mui/icons-material/Api';
import DnsIcon from '@mui/icons-material/Dns';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtGcc(val) {
  if (val === null || val === undefined) return '—';
  if (val < 0.0001) return '0.00 GCC';
  if (val < 0.01) return `${val.toFixed(4)} GCC`;
  return `${val.toFixed(2)} GCC`;
}

function barPct(part, total) {
  if (!total) return 0;
  return Math.max((part / total) * 100, part > 0 ? 0.5 : 0);
}

function utilizationPct(used, allocated) {
  if (!allocated) return 0;
  return Math.min((used / allocated) * 100, 100);
}

function utilizationColor(pct) {
  if (pct >= 90) return 'error';
  if (pct >= 70) return 'warning';
  return 'primary';
}

function buildPeriodOptions() {
  const now = new Date();
  const options = [];
  for (let i = 0; i < 4; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleString('default', { month: 'long', year: 'numeric' });
    options.push({ value, label });
  }
  return options;
}

// ── Visual config ─────────────────────────────────────────────────────────────

const CATEGORY_CONFIG = {
  lambda_requests:  { color: '#E65100', Icon: ApiIcon },
  ec2_requests:     { color: '#1B5E20', Icon: DnsIcon },
  face_grouping:    { color: '#1565C0', Icon: FaceIcon },
  ai_search:        { color: '#6A1B9A', Icon: SearchIcon },
  image_captioning: { color: '#00838F', Icon: ImageIcon },
  storage_io:       { color: '#BF360C', Icon: CloudIcon },
  shared_infra:     { color: '#546E7A', Icon: StorageIcon },
};

// ── Sub-components ────────────────────────────────────────────────────────────

function BreakdownBar({ categories, total }) {
  if (!total) {
    return <Box sx={{ height: 14, borderRadius: 1, bgcolor: 'grey.200', width: '100%' }} />;
  }
  return (
    <Box sx={{ display: 'flex', width: '100%', height: 14, borderRadius: 1, overflow: 'hidden', gap: '1px', bgcolor: 'grey.200' }}>
      {categories.map(cat => {
        const cfg = CATEGORY_CONFIG[cat.key] || {};
        const w = barPct(cat.usd, total);
        if (w === 0) return null;
        return (
          <Tooltip key={cat.key} title={`${cat.label}: ${fmtGcc(cat.usd)}`} arrow>
            <Box sx={{ width: `${w}%`, bgcolor: cfg.color || '#888', transition: 'width 0.4s ease', minWidth: 2 }} />
          </Tooltip>
        );
      })}
    </Box>
  );
}

function CategoryRow({ cat }) {
  const cfg = CATEGORY_CONFIG[cat.key] || {};
  const Icon = cfg.Icon || StorageIcon;
  return (
    <Stack direction="row" alignItems="flex-start" spacing={1}>
      <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: cfg.color || '#888', flexShrink: 0, mt: 0.6 }} />
      <Icon sx={{ fontSize: 15, color: 'text.secondary', flexShrink: 0, mt: 0.3 }} />
      <Box sx={{ flex: 1 }}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography variant="body2" color="text.secondary">{cat.label}</Typography>
          {cat.is_fixed && (
            <Tooltip title="Fixed monthly estimate, not metered from actual usage" placement="top">
              <Chip label="est" size="small" variant="outlined" sx={{ height: 16, fontSize: 10, '& .MuiChip-label': { px: 0.75 } }} />
            </Tooltip>
          )}
        </Stack>
        {cat.detail && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', lineHeight: 1.3 }}>
            {cat.detail}
          </Typography>
        )}
      </Box>
      <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ minWidth: 68, textAlign: 'right', flexShrink: 0 }}>
        {fmtGcc(cat.usd)}
      </Typography>
    </Stack>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const UsageProfiler = ({ profileApi }) => {
  const periodOptions = buildPeriodOptions();

  const [selectedPeriod, setSelectedPeriod] = useState(periodOptions[0].value);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [devMode, setDevMode] = useState(false);

  const compute = async (period) => {
    setLoading(true);
    setError('');
    setDevMode(false);
    try {
      const result = await profileApi.getComputeUsage(period || selectedPeriod);
      setData(result);
    } catch (err) {
      const resp = err?.response?.data;
      if (resp?.dev_mode) {
        setDevMode(true);
        setError('');
      } else {
        setError(resp?.error || err.message || 'Failed to fetch usage data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (e) => {
    setSelectedPeriod(e.target.value);
    setData(null);
    setError('');
    setDevMode(false);
  };

  const categories      = data?.categories || [];
  const totalGcc        = data?.total_gcc || 0;
  const creditAlloc     = data?.credit_allocation || 10;
  const usedPct         = utilizationPct(totalGcc, creditAlloc);
  const barColor        = utilizationColor(usedPct);

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
      {/* Header */}
      <Stack direction="row" spacing={1.5} alignItems="center" mb={data ? 2 : 1.5}>
        <MonitorHeartIcon color="primary" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>Compute Usage</Typography>
          <Typography variant="caption" color="text.secondary">
            General compute credit utilisation
          </Typography>
        </Box>
        {data && (
          <Button
            size="small"
            variant="outlined"
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
            onClick={() => compute(selectedPeriod)}
            disabled={loading}
          >
            Refresh
          </Button>
        )}
      </Stack>

      {/* Period selector */}
      <TextField
        select
        size="small"
        label="Period"
        value={selectedPeriod}
        onChange={handlePeriodChange}
        sx={{ mb: 2, minWidth: 200 }}
      >
        {periodOptions.map(opt => (
          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
        ))}
      </TextField>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {devMode && (
        <Alert severity="info" icon={<LockIcon />} sx={{ mb: 2 }}>
          Usage logs are stored in CloudWatch and are not available in local development mode.
        </Alert>
      )}

      {/* Results */}
      {data && !loading && (
        <Box>
          {/* Utilisation headline */}
          <Stack direction="row" alignItems="baseline" spacing={1} mb={0.75}>
            <Typography variant="h5" fontWeight={700}>{fmtGcc(totalGcc)}</Typography>
            <Typography variant="body2" color="text.secondary">
              of {creditAlloc.toFixed(0)} GCC allocated
            </Typography>
            <Typography variant="body2" fontWeight={600} color={`${barColor}.main`}>
              ({usedPct.toFixed(1)}%)
            </Typography>
          </Stack>

          {/* Utilisation bar */}
          <Box mb={0.5}>
            <LinearProgress
              variant="determinate"
              value={usedPct}
              color={barColor}
              sx={{ height: 10, borderRadius: 1, bgcolor: 'grey.200' }}
            />
          </Box>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 2 }}>
            General compute credit 1 GCC 
          </Typography>

          {/* Per-category breakdown bar */}
          <Box mb={2}>
            <BreakdownBar categories={categories} total={totalGcc} />
          </Box>

          {/* Category rows */}
          <Stack spacing={1.5}>
            {categories.map(cat => (
              <CategoryRow key={cat.key} cat={cat} />
            ))}
          </Stack>

          <Stack direction="row" justifyContent="space-between" alignItems="center" mt={2}>
            <Tooltip
              title="Estimated general compute usage based on resource logs."
              placement="top"
            >
              <Typography variant="caption" color="text.disabled" sx={{ cursor: 'help', textDecoration: 'underline dotted' }}>
                ~estimated usage
              </Typography>
            </Tooltip>
            <Typography variant="caption" color="text.disabled">
              {data.event_count != null ? `${data.event_count} events · ` : ''}
              {data.query_duration_ms != null ? `query ${data.query_duration_ms}ms · ` : ''}
              {data.computed_at ? new Date(data.computed_at).toLocaleString() : ''}
            </Typography>
          </Stack>
        </Box>
      )}

      {/* Compute button */}
      {!data && !devMode && (
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <MonitorHeartIcon />}
          onClick={() => compute(selectedPeriod)}
          disabled={loading}
        >
          {loading ? 'Querying usage logs…' : 'Compute Usage'}
        </Button>
      )}

      {loading && data && (
        <Stack direction="row" spacing={1} alignItems="center" mt={1.5}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">Scanning usage logs…</Typography>
        </Stack>
      )}
    </Paper>
  );
};

export default UsageProfiler;
